import { useMemo, useState } from 'react';
import {
  FiCalendar,
  FiClock,
  FiEdit2,
  FiFolder,
  FiGrid, FiList,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiUser
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useMinistries } from '../../contexts/MinistryContext';
import { useUpdates } from '../../contexts/updatesContext';
import UploadService from '../../services/updateService';
import UploadForm from './forms/UpdateForm';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import DataTable from './ui/DataTable';
import Modal from './ui/Modal';

const UploadsManager = () => {
  const { updates, loading, error, setUpdates, currentUpdate, setCurrentUpdate } = useUpdates();
  const { ministries } = useMinistries();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedMinistry, setSelectedMinistry] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter and sort data
  const filteredUpdates = useMemo(() => {
    let filtered = updates.filter(update => {
      const matchesSearch = update.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         update.speaker.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = selectedType === 'all' || update.type === selectedType;
      
      let updateMinistry = '';
      if (Array.isArray(update.ministry)) {
        updateMinistry = update.ministry.map(m => m.name || m).join(', ').toLowerCase();
      } else if (typeof update.ministry === 'object' && update.ministry !== null) {
        updateMinistry = (update.ministry.name || 'general').toLowerCase();
      } else {
        updateMinistry = (update.ministry || 'general').toLowerCase();
      }
      
      const matchesMinistry = selectedMinistry === 'all' || updateMinistry.includes(selectedMinistry.toLowerCase());
      
      return matchesSearch && matchesType && matchesMinistry;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  }, [updates, searchTerm, selectedType, selectedMinistry, sortBy, sortOrder]);

  const stats = useMemo(() => {
    const total = updates.length;
    const sermons = updates.filter(u => u.type === 'sermon').length;
    const events = updates.filter(u => u.type === 'event').length;
    const recent = updates.filter(u => {
      const updateDate = new Date(u.date);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return updateDate >= weekAgo;
    }).length;

    return { total, sermons, events, recent };
  }, [updates]);

  const columns = [
    { 
      field: 'title', 
      header: 'Title', 
      width: '25%',
      render: (row) => (
        <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
          {row.title}
        </div>
      )
    },
    { 
      field: 'type', 
      header: 'Type', 
      width: '15%',
      render: (row) => (
        <span className="capitalize px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
          {row.type}
        </span>
      )
    },
    { 
      field: 'speaker', 
      header: 'Speaker', 
      width: '20%',
      render: (row) => (
        <div className="flex items-center">
          <FiUser className="mr-2 text-gray-400" size={14} />
          <span>{row.speaker}</span>
        </div>
      )
    },
    { 
      field: 'date', 
      header: 'Date', 
      type: 'date', 
      width: '15%',
      render: (row) => (
        <div className="flex items-center">
          <FiCalendar className="mr-2 text-gray-400" size={14} />
          <span>{new Date(row.date).toLocaleDateString()}</span>
        </div>
      )
    },
    { 
      field: 'ministry', 
      header: 'Ministry', 
      width: '15%',
      render: (row) => {
        const ministryName = Array.isArray(row.ministry) 
          ? row.ministry.map(m => m.name || m).join(', ')
          : typeof row.ministry === 'object' 
            ? row.ministry?.name || 'General'
            : row.ministry || 'General';
        
        return (
          <div className="flex items-center">
            <FiFolder className="mr-2 text-gray-400" size={14} />
            <span>{ministryName}</span>
          </div>
        );
      }
    },
    {
      field: 'actions',
      header: 'Actions',
      width: '10%',
      render: (row) => (
        <div className="flex space-x-2">
          <button 
            onClick={() => handleEdit(row)}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <FiEdit2 size={16} />
          </button>
          <button 
            onClick={() => handleDeleteClick(row)}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  const handleDeleteClick = (upload) => {
    setCurrentUpdate(upload);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      setProcessing(true);
      await UploadService.deleteUpdate(currentUpdate.id);
      setUpdates(updates.filter(item => item.id !== currentUpdate.id));
      toast.success('Upload deleted successfully');
    } catch (error) {
      toast.error(`Failed to delete upload: ${error.message}`);
    } finally {
      setProcessing(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleEdit = (upload) => {
    const normalizedUpload = {
      ...upload,
      ministry: Array.isArray(upload.ministry) 
        ? (upload.ministry[0]?.name || 'general')
        : (typeof upload.ministry === 'object' && upload.ministry !== null)
          ? (upload.ministry.name || 'general')
          : (upload.ministry || 'general')
    };
    setCurrentUpdate(normalizedUpload);
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      setProcessing(true);
      let updatedUploads;
      
      if (currentUpdate) {
        const updated = await UploadService.updateUpdate(currentUpdate.id, formData);
        updatedUploads = updates.map(u => 
          u.id === currentUpdate.id ? updated : u
        );
        toast.success('Upload updated successfully');
      } else {
        const created = await UploadService.createUpdate(formData);
        updatedUploads = [...updates, created];
        toast.success('Upload created successfully');
      }
      
      setUpdates(updatedUploads);
      setIsModalOpen(false);
    } catch (error) {
      toast.error(`Failed to save upload: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const renderGridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {filteredUpdates.map((upload) => (
        <div key={upload.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:border-blue-100 group">
          <div className="relative overflow-hidden rounded-t-xl aspect-video">
            {upload.image_thumbnail ? (
              <img 
                src={upload.image_thumbnail} 
                alt={upload.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                <FiUpload className="text-gray-300" size={48} />
              </div>
            )}
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 text-xs font-medium bg-white rounded-full text-gray-700 capitalize shadow-sm">
                {upload.type}
              </span>
            </div>
          </div>
          
          <div className="p-4">
            <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
              {upload.title}
            </h3>
            
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <FiUser className="mr-2 text-gray-400" size={14} />
                <span className="truncate">{upload.speaker}</span>
              </div>
              <div className="flex items-center">
                <FiCalendar className="mr-2 text-gray-400" size={14} />
                <span>{new Date(upload.date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center">
                <FiFolder className="mr-2 text-gray-400" size={14} />
                <span className="truncate">
                  {Array.isArray(upload.ministry) 
                    ? upload.ministry.map(m => m.name || m).join(', ')
                    : (typeof upload.ministry === 'object' && upload.ministry !== null)
                      ? (upload.ministry.name || 'General')
                      : (upload.ministry || 'General')
                  }
                </span>
              </div>
            </div>
            
            <div className="flex justify-between mt-4 pt-4 border-t border-gray-100">
              <button 
                onClick={() => handleEdit(upload)}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700"
              >
                <FiEdit2 className="mr-1" size={14} />
                Edit
              </button>
              <button 
                onClick={() => handleDeleteClick(upload)}
                className="flex items-center text-sm text-red-600 hover:text-red-700"
              >
                <FiTrash2 className="mr-1" size={14} />
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                <FiUpload className="mr-2 text-blue-600" size={24} />
                Content Management
              </h1>
              <p className="text-gray-600">
                Manage your uploads, sermons, and announcements
              </p>
            </div>
            
            <Button 
              onClick={() => {
                setCurrentUpdate(null);
                setIsModalOpen(true);
              }}
              variant="primary"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm"
            >
              <FiPlus className="mr-2" />
              New Upload
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-xs">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-gray-500 text-sm flex items-center">
                <FiFolder className="mr-1" size={14} />
                Total Uploads
              </div>
            </div>
            <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-xs">
              <div className="text-2xl font-bold text-gray-900">{stats.sermons}</div>
              <div className="text-gray-500 text-sm flex items-center">
                <FiUser className="mr-1" size={14} />
                Sermons
              </div>
            </div>
            <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-xs">
              <div className="text-2xl font-bold text-gray-900">{stats.events}</div>
              <div className="text-gray-500 text-sm flex items-center">
                <FiCalendar className="mr-1" size={14} />
                Events
              </div>
            </div>
            <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-xs">
              <div className="text-2xl font-bold text-gray-900">{stats.recent}</div>
              <div className="text-gray-500 text-sm flex items-center">
                <FiClock className="mr-1" size={14} />
                This Week
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <FiSearch size={18} />
              </div>
              <input
                type="text"
                placeholder="Search uploads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-32"
              >
                <option value="all">All Types</option>
                <option value="sermon">Sermons</option>
                <option value="event">Events</option>
                <option value="news">News</option>
                <option value="announcement">Announcements</option>
              </select>

              <select
                value={selectedMinistry}
                onChange={(e) => setSelectedMinistry(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-32"
              >
                <option value="all">All Ministries</option>
                <option value="general">General</option>
                {ministries.map(ministry => (
                  <option key={ministry.name} value={ministry.name.toLowerCase()}>
                    {ministry.name}
                  </option>
                ))}
              </select>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 flex items-center ${
                    viewMode === 'table' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <FiList className="mr-1" size={14} />
                  Table
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 flex items-center ${
                    viewMode === 'grid' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <FiGrid className="mr-1" size={14} />
                  Grid
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-center text-red-600">
              <FiTrash2 className="mr-2" size={18} />
              <p className="font-medium">
                {error.message || 'Failed to load updates'}
              </p>
            </div>
          </div>
        )}

        {/* Content Display */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {viewMode === 'grid' ? (
            <div className="p-6">
              {filteredUpdates.length === 0 ? (
                <div className="text-center py-12">
                  <FiUpload className="mx-auto text-gray-300 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No uploads found</h3>
                  <p className="text-gray-600">Try adjusting your search or filters</p>
                </div>
              ) : (
                renderGridView()
              )}
            </div>
          ) : (
            <DataTable
              data={filteredUpdates}
              columns={columns}
              loading={loading}
              emptyMessage={
                <div className="text-center py-12">
                  <FiUpload className="mx-auto text-gray-300 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No updates found</h3>
                  <p className="text-gray-600">Try adjusting your search or filters</p>
                </div>
              }
            />
          )}
        </div>

        {/* Results Count */}
        {filteredUpdates.length > 0 && (
          <div className="text-center text-gray-600 text-sm">
            Showing {filteredUpdates.length} of {updates.length} uploads
          </div>
        )}
      </div>

      {/* Modals */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <div className="flex items-center">
            {currentUpdate ? (
              <>
                <FiEdit2 className="mr-2 text-blue-600" size={20} />
                Edit Upload
              </>
            ) : (
              <>
                <FiPlus className="mr-2 text-blue-600" size={20} />
                New Upload
              </>
            )}
          </div>
        }
        size="lg"
      >
        <UploadForm
          initialValues={currentUpdate || {
            title: '',
            type: 'sermon',
            speaker: '',
            date: new Date(),
            video_url: '',
            image_thumbnail: '',
            ministry: 'general'
          }}
          ministriesOptions={[
            {value: 'general', label: 'General'}, 
            ...ministries.map(m => ({
              value: m.name.toLowerCase(),
              label: m.name
            }))
          ]}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={processing}
        />
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={
          <div className="flex items-center">
            <FiTrash2 className="mr-2 text-red-600" size={18} />
            Confirm Delete
          </div>
        }
        message={`Are you sure you want to delete "${currentUpdate?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        processing={processing}
      />
    </div>
  );
};

export default UploadsManager;